import LangEnum from '../../../lang/LangEnum';
import { formatDate } from '../../../utils/dates';
import { StatusType } from '../../orders/order.types';
import { useTranslation } from '../../translation/TranslationContext';
import {
  TargetConversionStatus,
  TargetConversionView,
} from '../components/TargetConversionCell/targetConversionCell.types';
import { TARGET_DATE_FORMAT } from '../target.constants';
import { ToConversionViewParams } from './useTargetConversionView.types';

const ZERO_VALUE = '0';
const NO_CONVERSION = 0;

const ATTAINMENT_STATUS_ORDER: StatusType[] = [
  StatusType.DRAFT,
  StatusType.IN_REVISION,
  StatusType.ON_APPROVAL,
  StatusType.APPROVED,
  StatusType.SENT,
];

const useTargetConversionView = () => {
  const { translate, locale } = useTranslation();

  const formatConversion = (conversion: number): string =>
    `${conversion.toLocaleString(locale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })}%`;

  const formatUpdatedAt = (calculatedAt: string | null): string | null => {
    if (!calculatedAt) {
      return null;
    }

    return translate(LangEnum.TARGET_LIST_CONVERSION_UPDATED_AT, {
      date: formatDate(calculatedAt, TARGET_DATE_FORMAT),
      time: formatDate(calculatedAt, 'HH:mm'),
    });
  };

  const toStatuses = ({
    attainedByStatus,
    calculatedAt,
    formatValue,
  }: ToConversionViewParams): TargetConversionStatus[] => {
    const byStatus = attainedByStatus ?? {};
    const statuses = ATTAINMENT_STATUS_ORDER.flatMap((status) => {
      const attained = byStatus[status];
      if (!attained) {
        return [];
      }

      return [
        {
          status,
          value: formatValue(attained.value),
          conversion: formatConversion(attained.conversion),
        },
      ];
    });

    if (statuses.length > 0) {
      return statuses;
    }

    if (!calculatedAt) {
      return [];
    }

    return [
      {
        status: null,
        value: formatValue(ZERO_VALUE),
        conversion: formatConversion(NO_CONVERSION),
      },
    ];
  };

  const toConversionView = (params: ToConversionViewParams): TargetConversionView => ({
    conversion: params.conversion,
    byStatus: toStatuses(params),
    updatedAt: formatUpdatedAt(params.calculatedAt),
  });

  return { toConversionView };
};

export default useTargetConversionView;
